/* assets/section-product.js */

class QuantitySelector extends HTMLElement {
  constructor() {
    super();

    this.onQuantityDecreaseListener = this.decreaseQuantity.bind(this);
    this.onQuantityIncreaseListener = this.increaseQuantity.bind(this);
    this.onInputChangeListener = this.validateQuantity.bind(this);
  }

  selectors = {
    input: "input",
    button: {
      decrease: "[data-action='decrease-quantity']",
      increase: "[data-action='increase-quantity']"
    }
  };

  connectedCallback() {
    this.inputElement = this.querySelector(this.selectors.input);
    this.decreaseButtonElement = this.querySelector(this.selectors.button.decrease);
    this.increaseButtonElement = this.querySelector(this.selectors.button.increase);

    this.customDelegate("click", this.selectors.button.decrease, this.onQuantityDecreaseListener);
    this.customDelegate("click", this.selectors.button.increase, this.onQuantityIncreaseListener);

    this.inputElement.addEventListener("change", this.onInputChangeListener);

    this.validateQuantity();
  }

  decreaseQuantity() {
    this.inputElement.value = Math.max(parseInt(this.inputElement.value) - 1, 1);
    this.forceInputChange();
  }

  increaseQuantity() {
    this.inputElement.value = parseInt(this.inputElement.value) + 1;
    this.forceInputChange();
  }

  validateQuantity() {
    this.inputElement.value = Math.max(parseInt(this.inputElement.value) || 1, 1);

    this.decreaseButtonElement.disabled =
      parseInt(this.inputElement.value) - 1 < parseInt(this.inputElement.min);

    this.increaseButtonElement.disabled =
      parseInt(this.inputElement.value) + 1 > parseInt(this.inputElement.max);
  }

  forceInputChange() {
    this.inputElement.dispatchEvent(new Event("change"));
  }
}

if (!customElements.get("quantity-selector")) {
  customElements.define("quantity-selector", QuantitySelector);
}

class ProductFormActions extends HTMLElement {
  constructor() {
    super();

    this.onVariantChangedListener = this.onVariantChange.bind(this);
    this.onVariantAddedListener = this.onVariantAdd.bind(this);
  }

  selectors = {
    submit: "[data-add-to-cart]",
    submitText: "[data-add-to-cart-text]",
    message: "[data-product-form-message]"
  };

  attributes = {
    form: "form"
  };

  datasets = {
    minimumStockThreshold: "minimumStockThreshold"
  };

  modifiers = {
    adding: "adding",
    hidden: "hide",
    message: {
      error: "message--error",
      success: "message--success"
    }
  };

  messageTypes = {
    success: "success",
    error: "error"
  };

  connectedCallback() {
    this.productForm = document.forms[this.getAttribute(this.attributes.form)];

    this.submitButton = this.querySelector(this.selectors.submit);
    this.submitButtonText = this.querySelector(this.selectors.submitText);

    this.productFormMessage = this.querySelector(this.selectors.message);

    this.minimumStockThreshold = parseInt(this.dataset[this.datasets.minimumStockThreshold]);

    this.productForm?.addEventListener("variant:change", this.onVariantChangedListener);
    this.productForm?.addEventListener("variant:add", this.onVariantAddedListener);
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener("variant:change", this.onVariantChangedListener);
    this.productForm?.removeEventListener("variant:add", this.onVariantAddedListener);
  }

  onVariantChange(event) {
    const variant = event.detail.variant;

    if (!this.submitButton || !this.submitButtonText) return;

    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButtonText.innerText = theme.strings.product.unavailable;
      return;
    }

    const inventoryPolicy = variant?.inventory_policy;
    const inventoryQuantity = parseInt(variant.inventory_quantity);

    if (inventoryPolicy === "continue") {
      this.submitButton.disabled = false;
      this.submitButtonText.innerText = theme.strings.product.add_to_cart;
      return;
    }

    if (!variant.available || inventoryQuantity <= this.minimumStockThreshold) {
      this.submitButton.disabled = true;
      this.submitButtonText.innerText = theme.strings.product.sold_out;
    } else {
      this.submitButton.disabled = false;
      this.submitButtonText.innerText = theme.strings.product.add_to_cart;
    }
  }

  async onVariantAdd(event) {
    const { variant, formData } = event?.detail ?? {};
    if (!formData) return;

    this.updateProductFormMessage();

    const url = `${window.theme.routes.cart}/add.js`;
    this.submitButton.classList.add(this.modifiers.adding);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({ items: [{ ...formData }] }),
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const data = await response.json();
      const { status, description } = data;

      if (status === 422) {
        this.onVariantAddedError(description);
        return;
      }

      this.onVariantAddedSuccess(data, variant);
    } catch (error) {
      this.onVariantAddedError(error);
    }

    setTimeout(() => {
      this.submitButton.classList.remove(this.modifiers.adding);
    }, 1500);
  }

  disableButton() {
    if (this.submitButton) this.submitButton.disabled = true;
  }

  enableButton() {
    if (this.submitButton) this.submitButton.disabled = false;
  }

  forceSelection() {
    if (this.submitButtonText) this.submitButtonText.innerText = theme.strings.product.complete_selection;
  }

  onVariantAddedSuccess(_data, variant) {
    document.dispatchEvent(
      new CustomEvent("theme:cart:popdown", {
        detail: { variant }
      })
    );

    this.updateCart();
  }

  onVariantAddedError(error) {
    console.error(error);
    this.updateProductFormMessage(this.messageTypes.error, error);
  }

  updateProductFormMessage(type = null, message = "") {
    if (!this.productFormMessage) return;

    this.productFormMessage.innerHTML = message;

    switch (type) {
      case this.messageTypes.success:
        this.productFormMessage.classList.remove(this.modifiers.message.error);
        this.productFormMessage.classList.add(this.modifiers.message.success);
        break;

      case this.messageTypes.error:
        this.productFormMessage.classList.remove(this.modifiers.message.success);
        this.productFormMessage.classList.add(this.modifiers.message.error);
        break;

      default:
        this.productFormMessage.classList.add(this.modifiers.hidden);
    }

    this.productFormMessage.classList.toggle(this.modifiers.hidden, !message);
  }

  async updateCart() {
    const url = `${Shopify.routes.root}cart.js`;
    const response = await fetch(url).then((r) => r.json());

    document.dispatchEvent(
      new CustomEvent("theme:cart:change", {
        detail: { cart: response },
        bubbles: true
      })
    );
  }
}

if (!customElements.get("product-form-actions")) {
  customElements.define("product-form-actions", ProductFormActions);
}

class CompareAtPrice extends HTMLElement {
  constructor() {
    super();
    this.onVariantChangedListener = this.onVariantChange.bind(this);
  }

  modifiers = { hidden: "hide" };

  connectedCallback() {
    const formId = this.getAttribute("form");
    this.form = document.forms[formId];
    this.form?.addEventListener("variant:change", this.onVariantChangedListener);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onVariantChangedListener);
  }

  onVariantChange(event) {
    const variant = event.detail.variant;
    if (!variant) return;

    if (variant.compare_at_price > variant.price) {
      this.innerText = theme.formatMoney(variant.compare_at_price, theme.moneyFormat);
      this.classList.remove(this.modifiers.hidden);
    } else {
      this.innerText = "";
      this.classList.add(this.modifiers.hidden);
    }
  }
}

if (!customElements.get("compare-at-price")) {
  customElements.define("compare-at-price", CompareAtPrice);
}

class RegularPrice extends HTMLElement {
  constructor() {
    super();
    this.onVariantChangedListener = this.onVariantChange.bind(this);
  }

  modifiers = { sale: "price--sale" };

  connectedCallback() {
    const formId = this.getAttribute("form");
    this.form = document.forms[formId];
    this.form?.addEventListener("variant:change", this.onVariantChangedListener);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("variant:change", this.onVariantChangedListener);
  }

  onVariantChange(event) {
    const variant = event.detail.variant;
    if (!variant) return;

    this.innerText = theme.formatMoney(variant.price, theme.moneyFormat);

    if (variant.compare_at_price > variant.price) {
      this.classList.add(this.modifiers.sale);
    } else {
      this.classList.remove(this.modifiers.sale);
    }
  }
}

if (!customElements.get("regular-price")) {
  customElements.define("regular-price", RegularPrice);
}

class ProductInfo extends HTMLElement {
  constructor() {
    super();

    this._hoverCache = {};
    this._originalMainSrc = null;
    this._hoverActive = false;
  }

  selectors = {
    form: { container: "[data-product-form]", submit: "product-form-actions" },
    stitchedProductsJson: "[data-stitched-products-json]",
    additionalVariantsDataJson: "[data-additional-variants-data]"
  };

  datasets = {
    productHandle: "productHandle",
    enableHistoryState: "enableHistoryState",
    enableProductStitching: "enableProductStitching",
    minimumStockThreshold: "minimumStockThreshold",
    option: "option",
    optionName: "optionName",
    index: "index"
  };

  modifiers = {
    disabled: "disabled"
  };

  colorOptionLabels = "color,colour,couleur,colore,farbe,색,色,fxärg,farve".split(",");
  sizeOptionLabels = "size,taille,größe,tamanho,tamaño,koko,サイズ".split(",");

  async connectedCallback() {
    this.product = await this.initProduct();
    if (!Object.keys(this.product).length) return;

    this.initElements();
    this.initProductForm();

    this.enableHistoryState = this.dataset[this.datasets.enableHistoryState] === "true";
    this.enableProductStitching = this.dataset[this.datasets.enableProductStitching] === "true";

    this.minimumStockThreshold = parseInt(this.dataset[this.datasets.minimumStockThreshold]);

    this.stitchedProducts = this.initStitchedProducts();
    this.additionalVariantsData = this.initAdditionalVariantsData();

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();

    // ✅ NEW: hover behavior
    this.initSwatchHover();
  }

  initElements() {
    this.productFormElement = this.querySelector(this.selectors.form.container);
    this.productFormActions = this.querySelector(this.selectors.form.submit);
  }

  async initProduct() {
    const handle = this.dataset[this.datasets.productHandle];
    if (!handle) return {};

    const response = await fetch(`/products/${handle}.js`).then((r) => r.text());
    return JSON.parse(response);
  }

  initStitchedProducts() {
    if (!this.enableProductStitching) return {};
    const el = this.querySelector(this.selectors.stitchedProductsJson);
    if (!el) return {};
    return JSON.parse(el.innerHTML);
  }

  initAdditionalVariantsData() {
    const el = this.querySelector(this.selectors.additionalVariantsDataJson);
    if (!el) return {};
    return JSON.parse(el.innerHTML);
  }

  initProductForm() {
    if (!this.productFormElement) return null;

    this.productForm = new Shopify.theme.productForm.ProductForm(this.productFormElement, this.product, {
      onOptionChange: this.onOptionChange.bind(this),
      onFormSubmit: this.onFormSubmit.bind(this),
      onQuantityChange: () => {},
      onPropertyChange: () => {}
    });

    this.productOptionInputs = this.productForm.optionInputs.reduce((options, input) => {
      const option = input.dataset[this.datasets.option];
      const inputs = options[option] || [];
      return { ...options, [option]: [...inputs, input] };
    }, {});

    this.currentVariant = this.productForm.variant();
  }

  onOptionChange(event) {
    this.selectVariant(event.dataset.variant);

    if (this.productFormActions) this.productFormActions.updateProductFormMessage();

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();
    this.updateBrowserHistoryState();
  }

  onFormSubmit(event) {
    event.preventDefault();

    const areAllOptionsSelected = Object.values(this.productOptionInputs).every((inputs) =>
      inputs.some((input) => input.checked)
    );

    if (!areAllOptionsSelected) {
      this.productFormActions.disableButton();
      this.productFormActions.forceSelection();
      return;
    }

    this.productFormActions.enableButton();

    const form = event.target;
    const formData = new FormData(form);
    const formDataJson = this.formDataToJSON(formData);

    this.productFormElement.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          product: this.product,
          variant: this.currentVariant,
          formData: formDataJson
        }
      })
    );
  }

  selectVariant(variant) {
    const previousVariant = this.currentVariant;
    this.currentVariant = variant;

    if (this.additionalVariantsData.hasOwnProperty(this.currentVariant.id)) {
      this.currentVariant = { ...variant, ...this.additionalVariantsData[variant.id] };
    }

    this.productFormElement.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: { product: this.product, variant: this.currentVariant, previousVariant }
      })
    );
  }

  updateBrowserHistoryState() {
    if (!this.currentVariant || !this.enableHistoryState) return;
    const url = Shopify.theme.productForm.getUrlWithVariant(window.location.href, this.currentVariant.id);
    window.history.replaceState({ path: url }, "", url);
  }

  validateOptionSelection() {
    return Object.values(this.productOptionInputs).every((inputs) => inputs.some((input) => input.checked));
  }

  disableUnavailableOptions() {
    if (!Object.keys(this.productOptionInputs).length) return;
    if (!this.validateOptionSelection()) return;
    if (!this.currentVariant) return;

    const selectedOptions = this.getSelectedOptions();

    const productVariants = this.product.variants.map((variant) =>
      this.additionalVariantsData.hasOwnProperty(variant.id)
        ? { ...variant, ...this.additionalVariantsData[variant.id] }
        : variant
    );

    const availableOptions = this.getAvailableValues(productVariants, selectedOptions);

    Object.keys(this.productOptionInputs).forEach((option) => {
      const availableValues = availableOptions[option] ?? [];

      this.productOptionInputs[option].forEach((input) => {
        input.classList.toggle(this.modifiers.disabled, !availableValues.includes(input.value));
      });
    });
  }

  disableUnavailableStitchedProducts() {
    const stitchedProducts = Object.values(this.stitchedProducts);

    const hasProductOptions = Object.keys(this.productOptionInputs).length > 0;
    const hasStitchedProducts = stitchedProducts.length > 0;
    const areAllOptionsSelected = this.validateOptionSelection();

    const canCheckStitchedProducts = hasProductOptions && hasStitchedProducts && areAllOptionsSelected;
    if (!canCheckStitchedProducts) return;

    const selectedOptionValuesByName = this.getSelectedOptionValuesByName();

    for (const stitchedProduct of stitchedProducts) {
      const stitchedProductSwatch = this.querySelector(`[data-stitched-product-id='${stitchedProduct.id}']`);
      const colorOption = this.findColorOption(stitchedProduct.options);

      const canCheckStitchedProduct = stitchedProductSwatch && colorOption;
      if (!canCheckStitchedProduct) continue;

      const optionValuesToFind = this.matchSelectedOptionsByName(stitchedProduct, colorOption, selectedOptionValuesByName);

      const hasMatchedOptions = Object.keys(optionValuesToFind).length > 0;
      if (!hasMatchedOptions) continue;

      // Each linked product is checked in its own colour
      const colorOptionPosition = `option${colorOption.position}`;
      optionValuesToFind[colorOptionPosition] = stitchedProduct.current_variant[colorOptionPosition];

      const isStitchedProductAvailable = stitchedProduct.variants.some(
        (variant) => this.isVariantInStock(variant) && this.doesVariantMatchOptionValues(variant, optionValuesToFind)
      );

      stitchedProductSwatch.classList.toggle(this.modifiers.disabled, !isStitchedProductAvailable);
    }
  }

  findColorOption(options) {
    return options.find((option) => this.colorOptionLabels.includes(option.name.toLowerCase()));
  }

  // Returns the current selection keyed by the linked product's own option positions,
  // e.g. { option2: "XL" }. Options are matched by name, not position: a linked product can
  // store its options in a different order (e.g. Colour, Size) than the current product
  // (e.g. Size, Colour). Colour is skipped, since each linked product has its own colour.
  matchSelectedOptionsByName(stitchedProduct, colorOption, selectedOptionValuesByName) {
    const matchedOptionValues = {};

    for (const stitchedOption of stitchedProduct.options) {
      if (stitchedOption === colorOption) continue;

      const selectedValue = selectedOptionValuesByName[stitchedOption.name.toLowerCase()];
      if (!selectedValue) continue;

      matchedOptionValues[`option${stitchedOption.position}`] = selectedValue;
    }

    return matchedOptionValues;
  }

  // Same stock rule as getAvailableValues(): a variant at or below the minimum stock
  // threshold counts as out of stock, even if Shopify still reports it as available
  isVariantInStock(variant) {
    if (!variant.available) return false;
    if (variant.inventory_quantity <= this.minimumStockThreshold) return false;

    return true;
  }

  // Compares case-insensitively, since linked products don't always spell values the
  // same way (e.g. "xl" vs "XL")
  doesVariantMatchOptionValues(variant, optionValues) {
    return Object.entries(optionValues).every(([optionPosition, optionValue]) => {
      const variantValue = variant[optionPosition] ?? "";
      return variantValue.toLowerCase() === optionValue.toLowerCase();
    });
  }

  getSelectedOptionValuesByName() {
    return this.productForm.options().reduce((options, option) => {
      options[option.name.toLowerCase()] = option.value;
      return options;
    }, {});
  }

  getSelectedOptions() {
    return this.productForm.options().reduce((options, option, index) => {
      options[`option${index + 1}`] = option.value;
      return options;
    }, {});
  }

  getAvailableValues(variants, selected, minimumStockThreshold = this.minimumStockThreshold) {
    let originalSelectedKeys = Object.keys(selected);
    let maxOptionKey = `option${originalSelectedKeys.length + 1}`;

    let variantOptions = variants.map((variant) => {
      let keys = Object.keys(variant).filter((key) => key.indexOf("option") === 0 || key === "available" || key === "inventory_quantity");

      return Object.assign(
        {},
        {},
        ...keys.map((key) => {
          if (key === "available" || key === "inventory_quantity") return { [key]: variant[key] };
          if (key > maxOptionKey) return {};
          const value = variant[key];
          return value ? { [key]: value } : {};
        })
      );
    });

    Object.keys(variantOptions[0]).forEach((key) => {
      if (key === "available" || key === "inventory_quantity") return;
      if (selected.hasOwnProperty(key)) return;
      selected[key] = null;
    });

    let selectedKeys = Object.keys(selected);

    let combinations =
      selectedKeys.length > 0
        ? selectedKeys.map((excludeKey) => {
            return Object.assign(
              {},
              ...selectedKeys.map((key) => (excludeKey === key ? {} : { [key]: selected[key] }))
            );
          })
        : [{}];

    let availableValues = {};

    variantOptions
      .map((options) => {
        return Object.assign(
          {},
          {},
          ...combinations.map((combination) => {
            const isSubset =
              Object.keys(combination)
                .map((key) => {
                  if (combination[key] === null) return true;
                  return options.hasOwnProperty(key) && combination[key] === options[key];
                })
                .includes(false) !== true;

            if (!isSubset) return {};
            if (!options.available) return {};
            if (options.inventory_quantity <= minimumStockThreshold) return {};

            return Object.assign(
              {},
              {},
              ...Object.keys(options)
                .filter((key) => key !== "available" && key !== "inventory_quantity" && !combination.hasOwnProperty(key))
                .map((optionName) => ({ [optionName]: [options[optionName]] }))
            );
          })
        );
      })
      .filter((o) => Object.keys(o).length > 0)
      .forEach((o) => {
        Object.keys(o).forEach((key) => {
          if (!availableValues.hasOwnProperty(key)) availableValues[key] = [];
          availableValues[key] = availableValues[key]
            .concat(o[key])
            .filter((v, idx, self) => self.indexOf(v) === idx);
        });
      });

    return Object.assign({}, {}, ...Object.keys(availableValues).sort().map((key) => ({ [key]: availableValues[key] })));
  }

  formDataToJSON(formData) {
    return Array.from(formData.entries()).reduce((memo, [key, value]) => ({ ...memo, [key]: value }), {});
  }

  // -------------------------
  // ✅ HOVER IMAGE PREVIEW
  // -------------------------
  getMainImageElement() {
    const root = this.closest(".product") || document;

    // Do NOT rely on swiper-slide-active (your media slider isn't a swiper slide here)
    return root.querySelector(".product-slider img") ||
      root.querySelector("product-slider img") ||
      root.querySelector(".product-gallery img") ||
      root.querySelector("product-gallery img") ||
      root.querySelector("img");
  }

  async fetchProductJsonByHandle(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error("Hover fetch failed", e);
      return null;
    }
  }

  async previewImageFromHandle(handle) {
    if (!handle) return;

    if (!this._hoverCache[handle]) {
      this._hoverCache[handle] = this.fetchProductJsonByHandle(handle);
    }

    const data = await this._hoverCache[handle];
    if (!data) return;

    const imgUrl = data.featured_image || (data.images?.length ? data.images[0] : null);
    if (!imgUrl) return;

    const imgEl = this.getMainImageElement();
    if (!imgEl) return;

    if (!this._originalMainSrc) {
      this._originalMainSrc = imgEl.getAttribute("src") || imgEl.currentSrc || imgEl.src;
    }

    imgEl.setAttribute("src", imgUrl);
    imgEl.src = imgUrl;
    imgEl.removeAttribute("srcset");
  }

  restoreSelectedVariantMedia() {
    const imgEl = this.getMainImageElement();
    if (!imgEl) return;

    if (this._originalMainSrc) {
      imgEl.setAttribute("src", this._originalMainSrc);
      imgEl.src = this._originalMainSrc;
      imgEl.removeAttribute("srcset");
      this._originalMainSrc = null;
    }
  }

  initSwatchHover() {
    // mouseover
    this.addEventListener("mouseover", (e) => {
      const swatchSlide = e.target.closest("slider-slide[data-swatch-hover]");
      if (!swatchSlide || !this.contains(swatchSlide)) return;

      const labelEl = swatchSlide.closest(".product-option")?.querySelector("[data-swatch-hover-label]");
      if (labelEl) labelEl.textContent = swatchSlide.dataset.hoverValue || "";

      // stitched swatch = anchor
      const stitchedA = swatchSlide.querySelector("a[data-stitched-product-handle]");
      const handle = stitchedA?.dataset?.stitchedProductHandle;

      if (handle) {
        window.__lastHoverHandle = handle;
        this._hoverActive = true;
        this.previewImageFromHandle(handle);
      }
    });

    // mouseout
    this.addEventListener("mouseout", (e) => {
      const swatchSlide = e.target.closest("slider-slide[data-swatch-hover]");
      if (!swatchSlide || !this.contains(swatchSlide)) return;

      // ignore internal moves
      if (swatchSlide.contains(e.relatedTarget)) return;

      const labelEl = swatchSlide.closest(".product-option")?.querySelector("[data-swatch-hover-label]");
      if (labelEl) labelEl.textContent = "";

      if (this._hoverActive) {
        this._hoverActive = false;
        this.restoreSelectedVariantMedia();
      }
    });
  }


  // --- HOVER HELPERS (ProductInfo) ---

getHeroImageEl() {
  const root = this.closest(".product") || document;
  // visible main image is inside product-gallery on your theme
  return (
    root.querySelector("product-gallery img.product-media-item__image") ||
    root.querySelector(".product-gallery img.product-media-item__image") ||
    root.querySelector(".product img.product-media-item__image")
  );
}

setHeroImage(url) {
  const img = this.getHeroImageEl();
  if (!img || !url) return false;

  // store original once
  if (!this._hoverOriginal) {
    this._hoverOriginal = {
      src: img.getAttribute("src"),
      srcset: img.getAttribute("srcset")
    };
  }

  img.setAttribute("src", url);
  img.removeAttribute("srcset"); // IMPORTANT: prevent currentSrc overriding
  img.src = url;

  return true;
}

restoreHeroImage() {
  const img = this.getHeroImageEl();
  if (!img || !this._hoverOriginal) return;

  if (this._hoverOriginal.src) img.setAttribute("src", this._hoverOriginal.src);
  else img.removeAttribute("src");

  if (this._hoverOriginal.srcset) img.setAttribute("srcset", this._hoverOriginal.srcset);
  else img.removeAttribute("srcset");

  this._hoverOriginal = null;
}

async fetchProductJsonByHandle(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error("[Hover] fetch failed", e);
    return null;
  }
}

getImageUrlFromProductJson(productJson) {
  if (!productJson) return null;
  // product.js returns featured_image as string URL
  return productJson.featured_image || (productJson.images?.length ? productJson.images[0] : null);
}

getImageUrlForOptionValue(optionValue) {
  // For non-stitched radio swatches on same product:
  // Try to find a variant whose option1/2/3 matches the hovered value.
  if (!this.product || !this.product.variants || !optionValue) return null;

  const val = String(optionValue);

  const v = this.product.variants.find(variant =>
    [variant.option1, variant.option2, variant.option3].includes(val)
  );

  // In product.js, variant.featured_image might exist; fallback to product images
  return (
    v?.featured_image?.src ||
    v?.featured_image ||
    null
  );
}

initSwatchHover() {
  // stop double-binding
  if (this._swatchHoverBound) return;
  this._swatchHoverBound = true;

  this.addEventListener("mouseover", async (e) => {
    // ❶ mouseover
const slide = e.target.closest("[data-swatch-hover]");
    if (!slide || !this.contains(slide)) return;

    // label
    const labelEl = slide.closest(".product-option")?.querySelector("[data-swatch-hover-label]");
    if (labelEl) labelEl.textContent = (slide.dataset.hoverValue || "")
  .replace(/[_-]/g, " ")
  .toLowerCase()
  .replace(/\b\w/g, c => c.toUpperCase());

    // 1) stitched swatch: anchor with handle
    const stitchedA = slide.querySelector("a[data-stitched-product-handle]");
    const handle = stitchedA?.dataset?.stitchedProductHandle;

    if (handle) {
      this._hoverCache = this._hoverCache || {};
      if (!this._hoverCache[handle]) this._hoverCache[handle] = this.fetchProductJsonByHandle(handle);

      const productJson = await this._hoverCache[handle];
      const url = this.getImageUrlFromProductJson(productJson);
      if (url) this.setHeroImage(url);
      return;
    }

    // 2) non-stitched swatch (radio): try to map hovered value → variant image
    const hoveredValue = slide.dataset.hoverValue;
    const url = this.getImageUrlForOptionValue(hoveredValue);
    if (url) this.setHeroImage(url);
  });

  this.addEventListener("mouseout", (e) => {
    // ❷ mouseout
const slide = e.target.closest("[data-swatch-hover]");
    if (!slide || !this.contains(slide)) return;

    // ignore moves inside same slide
    if (slide.contains(e.relatedTarget)) return;

    const labelEl = slide.closest(".product-option")?.querySelector("[data-swatch-hover-label]");
    if (labelEl) labelEl.textContent = "";

    this.restoreHeroImage();
  });
}


}

if (!customElements.get("product-info")) {
  customElements.define("product-info", ProductInfo);
}

class ProductSlider extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
  }

  selectors = { slide: "slider-slide", zoomMedia: "zoom-media" };

  modifiers = { initialized: "initialized" };

  options = {
    slidesPerView: "auto",
    spaceBetween: 0,
    loop: true,
    on: {
      afterInit: () => {
        this.classList.add(this.modifiers.initialized);
      },
      slideChange: this.onSlideChange.bind(this)
    },
    resistance: true,
    resistanceRatio: 0,
    navigation: {
      prevEl: "slider-button[type='prev']",
      nextEl: "slider-button[type='next']"
    },
    pagination: {
      el: "slider-pagination",
      bulletClass: "slider__pagination-bullet"
    }
  };

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  connectedCallback() {
    this.initSlider();
  }

  disconnectedCallback() {
    this.destroySlider();
  }

  initSlider() {
    if (this.swiperInstance) return;
    this.swiperInstance = new window.Swiper(this, this.options);
  }

  destroySlider(deleteInstance = true, clearStyles = true) {
    if (!this.swiperInstance) return;
    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  onSlideChange() {
    const prev = this.slides.find((slide) => parseInt(slide.dataset.index) === parseInt(this.swiperInstance.previousIndex));
    const zoomMedia = prev?.querySelector(this.selectors.zoomMedia);
    if (zoomMedia) zoomMedia.zoomOut();
  }
}

if (!customElements.get("product-slider")) {
  customElements.define("product-slider", ProductSlider);
}